// Internal admin/staff Concierge page for a single athlete.
// File: frontend/src/pages/ConciergeNotesPage.js
// Tabbed shell (2026-10-07 handoff): sticky athlete header + eight tabs routed
// by the URL hash. This page owns the data, the write handlers and the header;
// each tab body lives in components/concierge/tabs/.

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import Button from '../components/Button';
import DocumentsPanel from '../components/DocumentsPanel';
import SmsThreadPanel from '../components/concierge/SmsThreadPanel';
import TodoPanel from '../components/concierge/TodoPanel';
import ConciergeReportsCard from '../components/concierge/ConciergeReportsCard';
import NotesTab from '../components/concierge/tabs/NotesTab';
import WellnessTab from '../components/concierge/tabs/WellnessTab';
import SchoolsTab from '../components/concierge/tabs/SchoolsTab';
import DetailsTab from '../components/concierge/tabs/DetailsTab';
import OverviewTab from '../components/concierge/tabs/OverviewTab';
import VideoTab from '../components/concierge/tabs/VideoTab';
import { formatSavedAgo } from '../lib/athleteEditorModel';
import { TABS, tabFromHash, classificationShort, conciergeTagText } from '../lib/conciergeTabs';
import { buildDoNext, attentionCount } from '../lib/conciergeAttentionModel';
import { utcTodayIso } from '../lib/conciergeTodoModel';
import '../components/Layout.css';
import '../components/athleteEditor/AthleteEditor.css';
import './ConciergePage.css';
import { RailFrame } from '../components/AdminShell';

const authCfg = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

function ConciergeNotesPageContent() {
  const { userId } = useParams();
  const navigate = useNavigate();
  const { API_URL, user: me } = useAuth();
  const cfg = authCfg();

  const [tab, setTab] = useState(() => tabFromHash(window.location.hash));
  const [notes, setNotes] = useState([]);
  const [owner, setOwner] = useState(null);
  // Server says whether THIS viewer may edit THIS athlete's Concierge page.
  const [canEdit, setCanEdit] = useState(true);
  const [eligibleOwners, setEligibleOwners] = useState([]);
  const [classification, setClassification] = useState('');
  // Contract term. `contractEnd` is users.concierge_expires_at — the same
  // column the CONCIERGE · ENDS tag reads, so editing it here moves the tag.
  // null means "not loaded yet" — distinct from '' meaning "no date set", which
  // the ENDS tag below has to tell apart so a cleared date clears the tag.
  const [contractStart, setContractStart] = useState(null);
  const [contractEnd, setContractEnd] = useState(null);
  // What the server last acknowledged for the contract dates, so a rejected
  // edit reverts to a stored value rather than to the invalid one just typed.
  const confirmedContract = useRef({ start: '', end: '' });
  const [lastLoginAt, setLastLoginAt] = useState(null);
  const [lastProfileChangeAt, setLastProfileChangeAt] = useState(null);
  const [lastManagedAt, setLastManagedAt] = useState(null);
  const [lastChange, setLastChange] = useState({ field: null, by: null });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [profile, setProfile] = useState(null);
  const [membership, setMembership] = useState(null);
  const [scheduleModalOpen, setScheduleModalOpen] = useState(false);
  const [scheduleSending, setScheduleSending] = useState('');
  const [scheduleMsg, setScheduleMsg] = useState(null);
  const [lastSavedAt, setLastSavedAt] = useState(null);
  const [, forceTick] = useState(0);
  // Newest first: the GET returns newest first and new entries are prepended.
  const [mentalChecks, setMentalChecks] = useState([]);
  const [todos, setTodos] = useState(null);
  const [todoHistory, setTodoHistory] = useState([]);
  const [smsRecent, setSmsRecent] = useState([]);

  const flashSaved = useCallback(() => setLastSavedAt(Date.now()), []);

  // Back/forward or a pasted link changes the hash without a click.
  useEffect(() => {
    const onHash = () => setTab(tabFromHash(window.location.hash));
    window.addEventListener('hashchange', onHash);
    return () => window.removeEventListener('hashchange', onHash);
  }, []);

  // replaceState keeps react-router's own history.state ({idx,key}) intact; passing
  // null there breaks the router's Back handling. replaceState so tab clicks do not
  // pile up in Back history.
  const goTab = useCallback((id) => {
    setTab(id);
    const url = `${window.location.pathname}${window.location.search}#${id}`;
    try { window.history.replaceState(window.history.state, '', url); } catch (e) { /* ignore */ }
  }, []);

  // Notes-only refresh for TodoPanel's monthly-summary save, which writes a
  // ConciergeNote server-side. Must NOT call load() — that also sets
  // `loading` and overwrites the contract-date fields and owner, which would
  // clobber an in-progress edit on this page.
  const refreshNotes = () => axios.get(`${API_URL}/concierge/${userId}`, cfg)
    .then(res => setNotes(Array.isArray(res.data.notes) ? res.data.notes : []))
    .catch(() => {});

  // Re-render each second so the saved indicator stays fresh.
  useEffect(() => {
    if (!lastSavedAt) return undefined;
    const t = setInterval(() => forceTick(n => n + 1), 1000);
    return () => clearInterval(t);
  }, [lastSavedAt]);

  const load = () => {
    setLoading(true);
    axios.get(`${API_URL}/concierge/${userId}`, cfg)
      .then(res => {
        setNotes(Array.isArray(res.data.notes) ? res.data.notes : []);
        setOwner(res.data.owner || null);
        setCanEdit(res.data.can_edit !== false);
        setEligibleOwners(Array.isArray(res.data.eligible_owners) ? res.data.eligible_owners : []);
        setClassification(res.data.classification || '');
        setContractStart(res.data.contract_started_at || '');
        setContractEnd(res.data.expires_at || '');
        confirmedContract.current = {
          start: res.data.contract_started_at || '',
          end: res.data.expires_at || '',
        };
        setLastLoginAt(res.data.last_login_at || null);
        setLastProfileChangeAt(res.data.last_profile_change_at || null);
        setLastManagedAt(res.data.last_managed_at || null);
        setLastChange({ field: res.data.last_profile_change_field || null, by: res.data.last_profile_change_by || null });
      })
      .catch(err => setError(err.response?.data?.detail || 'Failed to load concierge notes.'))
      .finally(() => setLoading(false));
  };
  useEffect(load, [userId, API_URL]);

  const loadProfile = () => {
    axios.get(`${API_URL}/athletes/${userId}/profile`, cfg).catch(() => ({ data: null })).then(res => setProfile(res.data || null));
    axios.get(`${API_URL}/admin/users/${userId}/membership`, cfg).catch(() => ({ data: null })).then(res => setMembership(res.data || null));
  };
  useEffect(loadProfile, [userId, API_URL]);

  const loadMental = () => {
    axios.get(`${API_URL}/concierge/${userId}/mental-checks`, cfg)
      .then(res => setMentalChecks(Array.isArray(res.data) ? res.data : []))
      .catch(() => {});
  };
  useEffect(loadMental, [userId, API_URL]);

  // useCallback on [userId, API_URL] so the effect and onTodoSaved can list them
  // honestly; the auth header is built per call (`cfg` is a fresh object each render).
  const loadTodos = useCallback(() => axios.get(`${API_URL}/concierge/${userId}/todos`, authCfg())
    .then(res => setTodos(res.data)).catch(() => {}), [userId, API_URL]);
  const loadTodoHistory = useCallback(() => axios.get(`${API_URL}/concierge/${userId}/todos/history`, authCfg())
    .then(res => setTodoHistory(Array.isArray(res.data) ? res.data : [])).catch(() => {}), [userId, API_URL]);
  const loadSmsRecent = useCallback(() => axios.get(`${API_URL}/concierge/${userId}/sms/recent`, { ...authCfg(), params: { limit: 50 } })
    .then(res => setSmsRecent(Array.isArray(res.data?.messages) ? res.data.messages : [])).catch(() => {}), [userId, API_URL]);
  useEffect(() => { loadTodos(); loadTodoHistory(); loadSmsRecent(); }, [loadTodos, loadTodoHistory, loadSmsRecent]);

  // TodoPanel / Overview task changes: refresh the page's copies too.
  const onTodoSaved = useCallback(() => { flashSaved(); loadTodos(); loadTodoHistory(); }, [flashSaved, loadTodos, loadTodoHistory]);

  // Promise-returning so a tab can clear its own draft on success.
  const saveCheck = (payload) => axios.post(`${API_URL}/concierge/${userId}/mental-checks`, payload, cfg)
    .then(res => { setMentalChecks(prev => [res.data, ...prev]); flashSaved(); return res.data; })
    .catch(err => { setError(err.response?.data?.detail || 'Failed to save the check-in.'); throw err; });

  const u = profile?.user || {};
  const athleteName = `${u.first_name || ''} ${u.last_name || ''}`.trim();
  const initials = `${(u.first_name || '')[0] || ''}${(u.last_name || '')[0] || ''}`.toUpperCase() || '—';

  const heroPhoto = useMemo(() => {
    if (!profile) return null;
    const idx = profile.primary_photo_index || 1;
    if (idx === 2) return profile.player_photo_2 || profile.player_photo;
    if (idx === 3) return profile.player_photo_3 || profile.player_photo;
    return profile.player_photo || profile.player_photo_2 || profile.player_photo_3;
  }, [profile]);

  // The ENDS tag and the "Contract end" field are the SAME column
  // (users.concierge_expires_at), so once this page has loaded that value it is
  // authoritative — otherwise editing the field would leave the tag showing
  // the date the separate membership fetch happened to see first.
  const conciergeEnds = contractEnd === null ? membership?.concierge_expires_at : (contractEnd || null);

  const addNote = (body, vis) => axios.post(`${API_URL}/concierge/${userId}/notes`,
    { body, visible_to_athlete: !!vis.visible_to_athlete, visible_to_coach: !!vis.visible_to_coach }, cfg)
    .then(res => { setNotes(prev => [res.data, ...prev]); flashSaved(); return res.data; })
    .catch(err => { setError(err.response?.data?.detail || 'Failed to add note.'); throw err; });

  const changeOwner = (e) => {
    const raw = e.target.value;
    const owner_user_id = raw === '' ? null : Number(raw);
    axios.put(`${API_URL}/concierge/${userId}/owner`, { owner_user_id }, cfg)
      .then(res => {
        setOwner(res.data.owner || null);
        if (res.data.sms_cell_removal === 'failed') {
          setError("Owner changed, but the previous owner's phone could not be removed from the family's text thread. Try again or contact support.");
        } else {
          flashSaved();
        }
      })
      .catch(err => setError(err.response?.data?.detail || 'Failed to update ownership.'));
  };

  const changeClassification = (e) => {
    const value = e.target.value;
    const prev = classification;
    setClassification(value);
    axios.put(`${API_URL}/concierge/${userId}/classification`, { classification: value || null }, cfg)
      .then(res => { setClassification(res.data.classification || ''); flashSaved(); })
      .catch(err => { setClassification(prev); setError(err.response?.data?.detail || 'Failed to update classification.'); });
  };

  // Persist the contract dates on BLUR, not on change. An <input type="date">
  // fires change for every partially-typed value, including the empty string,
  // so saving on change would PUT a null — clearing the stored date and
  // stamping "last managed" — on the way to a complete date. Same pattern the
  // visits editor uses for its date fields.
  //
  // Both dates go up together so the server can reject a start that falls after
  // the end. `confirmedContract` is what the SERVER last acknowledged, so a
  // rejection reverts to a date the database actually holds rather than to the
  // invalid one just typed.
  const saveContract = () => {
    const nextStart = contractStart ?? '';
    const nextEnd = contractEnd ?? '';
    const { start, end } = confirmedContract.current;
    // Tabbing through an untouched field must not mark the athlete as managed.
    if (nextStart === start && nextEnd === end) return;

    axios.put(`${API_URL}/concierge/${userId}/contract`,
      { contract_started_at: nextStart || null, expires_at: nextEnd || null }, cfg)
      .then(res => {
        const s = res.data.contract_started_at || '';
        const e = res.data.expires_at || '';
        confirmedContract.current = { start: s, end: e };
        setContractStart(s);
        setContractEnd(e);
        flashSaved();
      })
      .catch(err => {
        setContractStart(start);
        setContractEnd(end);
        setError(err.response?.data?.detail || 'Failed to update the contract dates.');
      });
  };

  const hasSchedulingLink = !!(me && (me.scheduling_link || '').trim());

  const sendSchedulingLink = (channel) => {
    setScheduleSending(channel);
    setScheduleMsg(null);
    axios.post(`${API_URL}/concierge/${userId}/send-scheduling-link`, { channel }, cfg)
      .then(res => {
        if (res.data.note) setNotes(prev => [res.data.note, ...prev]);
        const failed = res.data.failed ? `, ${res.data.failed} failed` : '';
        setScheduleMsg({ type: 'ok', text: `Sent via ${channel === 'sms' ? 'SMS' : 'email'} to ${res.data.sent} contact(s)${failed}.` });
      })
      .catch(err => setScheduleMsg({ type: 'error', text: err.response?.data?.detail || 'Failed to send.' }))
      .finally(() => setScheduleSending(''));
  };

  const savedAgo = formatSavedAgo(lastSavedAt, Date.now());
  // A partner edits a client's Concierge page only while that client has a paid
  // or comp subscription (2026-10-02); the server refuses their writes otherwise
  // (partner_scope.concierge_partner_view_only) and reports it as can_edit.
  const viewOnly = me?.role === 'partner' && !canEdit;

  const todayIso = utcTodayIso();
  const doNext = buildDoNext({ tasks: todos?.tasks || [], lastWellnessAt: mentalChecks[0]?.created_at || null, todayIso });
  const counts = { overview: attentionCount(doNext), todo: todos?.counts?.overdue || 0 };
  const tag = conciergeTagText({ isConcierge: !!membership?.is_concierge, endsIso: conciergeEnds, todayIso });
  const maxLevel = classificationShort(classification);
  const meta = [profile?.position_primary, profile?.graduation_year && `Class of ${profile.graduation_year}`,
    profile?.high_school && [profile.high_school, profile.high_school_state].filter(Boolean).join(', '),
    owner?.name && `Owner: ${owner.name}`].filter(Boolean).join(' · ');

  return (
    <>
      <header className="cn-head">
        <div className="cn-head__row">
          <button type="button" className="cn-head__back" aria-label="Back"
            onClick={() => (window.history.length > 1 ? navigate(-1) : navigate(`/athletes/${userId}`))}>←</button>
          {heroPhoto
            ? <img className="cn-head__avatar" src={heroPhoto} alt={athleteName || 'Athlete'} />
            : <div className="cn-head__avatar cn-head__avatar--initials" aria-hidden="true">{initials}</div>}
          <div className="cn-head__id">
            <div className="cn-head__nameline">
              <h1 className="cn-head__name">{athleteName || '(unnamed)'}</h1>
              {tag && <span className="cn-tag cn-tag--concierge">{tag}</span>}
              {maxLevel && <span className="cn-tag">MAX LEVEL · {maxLevel}</span>}
            </div>
            <div className="cn-head__meta">{meta}</div>
          </div>
          <div className="cn-head__actions">
            {savedAgo && <span className="cn-head__saved" title={`Saved ${savedAgo}`}>✓ Saved</span>}
            <button type="button" className="cn-hbtn" disabled={viewOnly}
              onClick={() => { setScheduleMsg(null); setScheduleModalOpen(true); }}>Send scheduling link</button>
            <button type="button" className="cn-hbtn cn-hbtn--dark"
              onClick={() => navigate(`/athletes/${userId}?edit=1`)}>Edit profile</button>
          </div>
        </div>
        <div className="cn-tabs" role="tablist" aria-label="Concierge sections">
          {TABS.map(t => (
            <button key={t.id} type="button" role="tab" id={`cn-tab-${t.id}`}
              aria-selected={tab === t.id} aria-controls={`cn-panel-${t.id}`}
              className={`cn-tab${tab === t.id ? ' cn-tab--on' : ''}`} onClick={() => goTab(t.id)}>
              {t.label}
              {counts[t.id] > 0 && <span className="cn-tab__count">{counts[t.id]}</span>}
            </button>
          ))}
        </div>
      </header>

      <div className="cn-body">
        {viewOnly && (
          <div className="cn-viewonly-banner" role="status" data-testid="concierge-view-only">
            View only — this athlete needs an active or comp subscription before you can edit their Concierge page.
          </div>
        )}
        {error && <div className="cn-page-err"><span className="cn-inline-msg cn-inline-msg--err">{error}</span></div>}
        {/* A disabled fieldset disables every form control inside it, in the
            child panels too, so no tab needs its own flag. The tab buttons sit
            outside it and stay clickable. */}
        <fieldset className="cn-viewonly" disabled={viewOnly} data-testid="concierge-fieldset">
          <div role="tabpanel" id={`cn-panel-${tab}`} aria-labelledby={`cn-tab-${tab}`}>
            {tab === 'overview' && (
              <OverviewTab userId={userId} cfg={cfg} API_URL={API_URL} athleteName={athleteName} profile={profile}
                lastLoginAt={lastLoginAt} lastProfileChangeAt={lastProfileChangeAt} lastChange={lastChange}
                doNext={doNext} notes={notes} smsRecent={smsRecent} mentalChecks={mentalChecks} todoHistory={todoHistory}
                onGoTab={goTab} onAddNote={addNote} onSaveCheck={saveCheck} onSmsSent={loadSmsRecent}
                onEditProfile={() => navigate(`/athletes/${userId}?edit=1`)} />
            )}
            {tab === 'todo' && (
              <div className="cn-tabbody">
                <TodoPanel userId={userId} onSaved={onTodoSaved} onNoteAdded={refreshNotes} />
              </div>
            )}
            {tab === 'texts' && (
              <div className="cn-tabbody">
                <SmsThreadPanel userId={userId} athleteName={athleteName} profile={profile} cfg={cfg} API_URL={API_URL}
                  onSent={loadSmsRecent} onScheduling={() => { setScheduleMsg(null); setScheduleModalOpen(true); }} />
              </div>
            )}
            {tab === 'notes' && <NotesTab notes={notes} loading={loading} onAdd={addNote} />}
            {tab === 'schools' && <SchoolsTab userId={userId} onSaved={flashSaved} profile={profile} />}
            {tab === 'wellness' && <WellnessTab checks={mentalChecks} onSave={saveCheck} />}
            {tab === 'documents' && (
              <div className="cn-tabbody">
                <section className="ae-card ae-editor-section">
                  <div className="cn-sechead">
                    <span className="cn-sechead__title">Documents</span>
                    <span className="cn-sechead__sub">Shared with athlete vs. internal only</span>
                  </div>
                  <div className="cn-doc-grid">
                    <DocumentsPanel variant="group" visibility="shared" asAthleteId={userId}
                      title="Shared documents" hint="Athlete + guardians can see" onSaved={flashSaved} />
                    <DocumentsPanel variant="group" internal visibility="admin_only" asAthleteId={userId}
                      title="Admin-only documents" hint="Staff only — never visible" onSaved={flashSaved} />
                  </div>
                </section>
              </div>
            )}
            {tab === 'video' && (
              <VideoTab userId={userId} onEditProfile={() => navigate(`/athletes/${userId}?edit=1`)} />
            )}
            {tab === 'details' && (
              <DetailsTab
                userId={userId} me={me} owner={owner} eligibleOwners={eligibleOwners}
                classification={classification} onClassification={changeClassification} onOwner={changeOwner}
                contractStart={contractStart} contractEnd={contractEnd}
                setContractStart={setContractStart} setContractEnd={setContractEnd} onContractBlur={saveContract}
                profile={profile} lastLoginAt={lastLoginAt} lastProfileChangeAt={lastProfileChangeAt}
                lastManagedAt={lastManagedAt} todayIso={todayIso}
                onEditProfile={() => navigate(`/athletes/${userId}?edit=1`)}
              />
            )}
          </div>
        </fieldset>
        {/* Outside the fieldset so view-only mode never disables it; on every tab. */}
        <div className="cn-reports">
          <ConciergeReportsCard userId={userId} API_URL={API_URL} cfg={cfg} />
        </div>
      </div>

      {scheduleModalOpen && (
        <div onClick={() => { if (!scheduleSending) setScheduleModalOpen(false); }}
          style={{ position: 'fixed', inset: 0, background: 'rgba(7,19,31,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 16 }}>
          <div onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: 4, border: '1px solid #D7DEE5', padding: 24, width: '100%', maxWidth: 460, boxShadow: '0 10px 40px rgba(7,19,31,0.2)' }}>
            <h3 style={{ margin: '0 0 6px', fontFamily: "'Oswald', sans-serif", fontSize: 18, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.02em', color: '#07131F' }}>Send scheduling link</h3>
            <p style={{ margin: '0 0 16px', color: '#6B7C8E', fontSize: 14 }}>
              Sends your scheduling link to {athleteName || 'the athlete'} and all guardians on file (every email or phone number associated with the athlete).
            </p>
            {!hasSchedulingLink ? (
              <div style={{ background: '#F7EEDD', border: '1px solid #E8D5AF', color: '#8A6420', borderRadius: 2, padding: '12px 14px', fontSize: 14 }}>
                You don't have a scheduling link set yet. Add one in <strong>Settings → My Account</strong> first.
              </div>
            ) : (
              <>
                <div style={{ fontSize: 13, color: '#4A5867', marginBottom: 14, wordBreak: 'break-all' }}>
                  Your link: <span style={{ color: '#07131F' }}>{me.scheduling_link}</span>
                </div>
                <div style={{ display: 'flex', gap: 10 }}>
                  <button className="cn-btn" onClick={() => sendSchedulingLink('sms')} disabled={!!scheduleSending}>Send SMS</button>
                  <button className="cn-btn-secondary" style={{ padding: '10px 20px', fontSize: 12 }} onClick={() => sendSchedulingLink('email')} disabled={!!scheduleSending}>Send Email</button>
                </div>
              </>
            )}
            {scheduleMsg && (
              <div style={{ marginTop: 14 }}>
                <span className={`cn-inline-msg cn-inline-msg--${scheduleMsg.type === 'ok' ? 'ok' : 'err'}`}>{scheduleMsg.text}</span>
              </div>
            )}
            <div style={{ marginTop: 18, textAlign: 'right' }}>
              <Button variant="secondary" size="sm" onClick={() => setScheduleModalOpen(false)} disabled={!!scheduleSending}>Close</Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

// Admin, staff and partner get the left rail around this page; every other role (coaches reach
// this route too) gets the page exactly as before. See RailFrame in components/AdminShell.js.
export default function ConciergeNotesPage() {
  return (
    <RailFrame active="concierge">
      <ConciergeNotesPageContent />
    </RailFrame>
  );
}
